import { useState, useEffect } from 'react';
import PractistSelect from '@/components/PractistSelect/PractistSelect';
import { SelectOption } from '../../types';
import { AddEditMatterFormData, Form } from '../types';
import styles from '../sharedStyles/InputField.module.css';
import { getInputPropsSafe, Paths } from '@/utils/getInputPropsSafe';
import { UseFormReturnType } from '@mantine/form';

interface ControlledSelectProps<T extends AddEditMatterFormData> {
  fieldName: Paths<T>;
  options: SelectOption[];
  form: UseFormReturnType<T>;
  clearable?: boolean;
  placeholder?: string;
  label: string;
}

const ControlledSelect = <T extends AddEditMatterFormData>({
  fieldName,
  options,
  form,
  clearable,
  placeholder,
  label,
}: ControlledSelectProps<T>) => {
  const [selectedValue, setSelectedValue] = useState<string | null>(
    form.values[fieldName as keyof typeof form.values] as string | null,
  );

  // Update local state when form value changes externally
  useEffect(() => {
    setSelectedValue(
      form.values[fieldName as keyof typeof form.values] as string | null,
    );
  }, [form.values[fieldName as keyof typeof form.values]]);

  return (
    <div className={styles.inputField}>
      <label>{label}</label>
      <PractistSelect
        clearable={clearable}
        data={options}
        // Render every option. `PractistSelect` otherwise caps the visible
        // list at 50 and, when a value is preselected (e.g. the new-matter
        // form defaults Responsible Attorney to the current user), windows the
        // list *around* that value — which silently hid every attorney earlier
        // in the alphabet. Attorney/user lists are small, so rendering all is
        // safe and keeps the full roster selectable.
        maxDropdownOptions={options.length}
        value={selectedValue ?? undefined}
        {...getInputPropsSafe(form, fieldName)}
        placeholder={placeholder}
        onOptionSelected={(value: string) => {
          setSelectedValue(value);
          // `setFieldValue`'s value parameter is typed as
          // `PathValue<Values, Field>` — a deep utility type internal to
          // @mantine/form. We know locally that this Select is bound to a
          // string-valued field, but TS can't narrow the form's union of
          // fee-type shapes here. Cast through `never` to satisfy the
          // generic without importing Mantine's non-exported helper.
          form.setFieldValue(fieldName, value as unknown as never);
        }}
      />
    </div>
  );
};

export default ControlledSelect;
